import { useState, useCallback, useMemo } from 'react';

import { useParams, useNavigate } from 'react-router-dom';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useIntl } from '@zenbusiness/application-commons-intl';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import ErrorState from '@zenbusiness/zui/ErrorState';
import LoadingSpinner from '@zenbusiness/zui/LoadingSpinner';
import PageContainer from '@zenbusiness/zui/PageContainer';
import PageLoading from '@zenbusiness/zui/PageLoading';
import Text from '@zenbusiness/zui/Text';

import { CONTACT_US_URL } from '#client/constants';

import {
  SubscriptionCancelActions,
  SubscriptionCancelConfirmation,
  SubscriptionCancelHeader,
  SubscriptionCancelHelp,
  SubscriptionBenefits,
  SubscriptionCancelReason,
  SubscriptionCancelResult,
  SubscriptionSummaryContent
} from './components';
import {
  StyledPageWrapper,
  StyledContentContainer,
  StyledSubscriptionCard,
  StyledContinueButtonRow
} from './SubscriptionCancelPage.styles';
import { useBillingSubscriptionChange } from '../../hooks/useBillingSubscriptionChange';
import { useBillingSubscriptions } from '../../hooks/useBillingSubscriptions';

const STEP_CONFIRM = 1;
const STEP_REASON = 2;
const STEP_SUBMIT = 3;
const STEP_LOADING = 4;
const STEP_RESULT = 5;

export default function SubscriptionCancelPage() {
  const { businessEntityUuid, orderBillingItemUuid } = useParams();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();

  const [step, setStep] = useState(STEP_CONFIRM);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [changeReason, setChangeReason] = useState('');
  const [changeReasonDisplay, setChangeReasonDisplay] = useState('');
  const [changeDescription, setChangeDescription] = useState('');

  const { data: subscriptions, loading, error } = useBillingSubscriptions();
  const { cancelSubscription } = useBillingSubscriptionChange();

  const subscription = useMemo(
    () => subscriptions.find((s) => s.orderBillingItemUuid === orderBillingItemUuid),
    [subscriptions, orderBillingItemUuid]
  );

  const dashboardPath = `/dashboard/${businessEntityUuid}`;
  const subscriptionsPath = `/dashboard/${businessEntityUuid}/subscription-hub/billing/subscriptions`;

  const trackCancelConfirm = useButtonPressedCallback({
    elementName: 'cancel_trial_confirm',
    page: 'subscription_cancel'
  });

  const trackCancelSubscription = useButtonPressedCallback({
    elementName: 'cancel_trial_submit',
    page: 'subscription_cancel'
  });

  const handleBack = useCallback(() => {
    if (step === STEP_LOADING || step === STEP_RESULT) return;
    if (step > STEP_CONFIRM) {
      setStep((s) => s - 1);
    } else {
      navigate(-1);
    }
  }, [step, navigate]);

  const handleClose = useCallback(() => {
    navigate(subscriptionsPath);
  }, [navigate, subscriptionsPath]);

  const handleConfirmContinue = useCallback(() => {
    trackCancelConfirm();
    setStep(STEP_REASON);
  }, [trackCancelConfirm]);

  const handleReasonContinue = useCallback(() => {
    setStep(STEP_SUBMIT);
  }, []);

  const handleSubmit = useCallback(async () => {
    trackCancelSubscription();
    setStep(STEP_LOADING);

    try {
      const result = await cancelSubscription({
        orderBillingItemUuid,
        changeType: 'cancel_renewal',
        changeReason,
        changeDescription: changeDescription || undefined
      });

      const statusCode = result?.data?.billingSubscriptionChange?.statusCode;
      const success = statusCode ? (statusCode >= 200 && statusCode < 300) : false;
      if (statusCode === undefined) {
        console.warn('SubscriptionCancelPage: billingSubscriptionChange response missing statusCode', result);
      }
      setSubmitSuccess(success);
      setStep(STEP_RESULT);
    } catch {
      setSubmitSuccess(false);
      setStep(STEP_RESULT);
    }
  }, [
    cancelSubscription,
    orderBillingItemUuid,
    changeReason,
    changeDescription,
    trackCancelSubscription
  ]);

  const handleReturnToSubscriptions = useCallback(() => {
    navigate(subscriptionsPath);
  }, [navigate, subscriptionsPath]);

  const handleReturnToDashboard = useCallback(() => {
    navigate(dashboardPath);
  }, [navigate, dashboardPath]);

  const handleContactUs = useCallback(() => {
    window.location.href = CONTACT_US_URL;
  }, []);

  if (loading) return <PageLoading />;

  if (error || !subscription) {
    return (
      <PageContainer>
        <ErrorState
          title={formatMessage({ id: 'dashboard.subscriptionCancel.error.loading.title' })}
          message={formatMessage({ id: 'dashboard.subscriptionCancel.error.loading.message' })}
        />
      </PageContainer>
    );
  }

  const canContinueReason = changeReason && changeDescription.length <= 255;

  return (
    <StyledPageWrapper>
      <SubscriptionCancelHeader onBack={step !== STEP_CONFIRM && step !== STEP_RESULT ? handleBack : undefined} onClose={handleClose} />

      <StyledContentContainer>
        {step === STEP_CONFIRM && (
          <>
            <Text size="2xl" weight="bold" family="secondary">
              {formatMessage({ id: 'dashboard.subscriptionCancel.confirm.heading' })}
            </Text>

            <StyledSubscriptionCard>
              <SubscriptionSummaryContent
                period={subscription.period}
                summary={subscription.summary}
                service={subscription.service}
                titleMessageId={subscription.titleMessageId}
                price={subscription.price}
                termStartDate={subscription.termStartDate}
                inTrial={subscription.inTrial}
                trialDays={subscription.trialDays}
              />
            </StyledSubscriptionCard>

            <SubscriptionBenefits
              benefits={subscription.benefits}
              benefitMessageIds={subscription.benefitMessageIds}
              service={subscription.service}
            />

            <SubscriptionCancelHelp />

            <SubscriptionCancelActions onConfirm={handleConfirmContinue} />
          </>
        )}

        {step === STEP_REASON && (
          <>
            <SubscriptionCancelReason
              changeReason={changeReason}
              changeDescription={changeDescription}
              onChangeReason={setChangeReason}
              onChangeReasonDisplay={setChangeReasonDisplay}
              onChangeDescription={setChangeDescription}
            />

            <StyledContinueButtonRow>
              <Button
                variant="contained"
                size="large"
                disabled={!canContinueReason}
                onClick={handleReasonContinue}
                sx={{ borderRadius: '12px', height: 56 }}
              >
                {formatMessage({ id: 'dashboard.subscriptionCancel.reason.continue' })}
              </Button>
            </StyledContinueButtonRow>
          </>
        )}

        {step === STEP_SUBMIT && (
          <SubscriptionCancelConfirmation
            subscription={subscription}
            changeReasonDisplay={changeReasonDisplay}
            onChangeReason={() => setStep(STEP_REASON)}
            onSubmit={handleSubmit}
          />
        )}

        {step === STEP_LOADING && (
          <Box display="flex" justifyContent="center" pt={6}>
            <LoadingSpinner
              title={formatMessage({ id: 'dashboard.subscriptionCancel.loading.title' })}
              subtitle={formatMessage({ id: 'dashboard.subscriptionCancel.loading.subtitle' })}
            />
          </Box>
        )}

        {step === STEP_RESULT && (
          <SubscriptionCancelResult
            success={submitSuccess}
            onReturnToSubscriptions={handleReturnToSubscriptions}
            onReturnToDashboard={handleReturnToDashboard}
            onContactUs={handleContactUs}
          />
        )}
      </StyledContentContainer>
    </StyledPageWrapper>
  );
}

